import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { InputRef } from "antd/es/input";
import { useTranslation } from "react-i18next";
import ReactGA from "react-ga4";

import QLayoutContext from "contexts/Layout";
import GAMap from "tokens/GAMap.json";

import { createSearchIndex, rankEntries } from "./ranking";
import { buildSearchEntries, resolveRecentEntries } from "./searchEntries";
import type { SearchEntry, UsageCounts } from "./types";

const RECENT_STORAGE_KEY = "qoyod.app_search.recent";
const USAGE_STORAGE_KEY = "qoyod.app_search.usage";
const MAX_RECENT = 5;

export const isMacPlatform = (): boolean =>
  typeof navigator !== "undefined" &&
  /mac|iphone|ipad/i.test(
    (navigator as { userAgentData?: { platform?: string } }).userAgentData?.platform ??
      navigator.platform,
  );

const trackGAEvent = (action: string) => {
  ReactGA.event({ category: GAMap.header.CATEGORY, action });
};

// Reads a JSON value from localStorage, mapping missing or corrupt data to the
// parser's fallback.
const readStored = <T>(key: string, parse: (value: unknown) => T): T => {
  try {
    return parse(JSON.parse(window.localStorage.getItem(key) ?? "null"));
  } catch {
    return parse(null);
  }
};

const persistStored = (key: string, value: unknown) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Private mode / storage quota — recents and usage degrade to session-only.
  }
};

const parseRecentIds = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((id): id is string => typeof id === "string") : [];

const parseUsageCounts = (value: unknown): UsageCounts =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (Object.fromEntries(
        Object.entries(value).filter(([, count]) => typeof count === "number"),
      ) as UsageCounts)
    : {};

interface UseAppSearchOptions {
  compact: boolean;
}

// Owns all app-search behavior: builds the entry index from the layout context,
// tracks query/dropdown/drawer state, ranks results, remembers recent picks,
// and navigates on selection.
export const useAppSearch = ({ compact }: UseAppSearchOptions) => {
  const { t } = useTranslation();
  const layoutContext = useContext(QLayoutContext);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [recentIds, setRecentIds] = useState<string[]>(() =>
    readStored(RECENT_STORAGE_KEY, parseRecentIds),
  );
  const [usageCounts, setUsageCounts] = useState<UsageCounts>(() =>
    readStored(USAGE_STORAGE_KEY, parseUsageCounts),
  );
  const inputRef = useRef<InputRef>(null);
  const noResultsTracked = useRef(false);

  const entries = useMemo(
    () => buildSearchEntries(layoutContext.sideMenuData, layoutContext.appSearchItems, t),
    [layoutContext.sideMenuData, layoutContext.appSearchItems, t],
  );

  const searchIndex = useMemo(() => createSearchIndex(entries), [entries]);

  const results = useMemo(
    () => rankEntries(searchIndex, query, usageCounts),
    [searchIndex, query, usageCounts],
  );

  const recentEntries = useMemo(
    () => resolveRecentEntries(entries, recentIds).slice(0, MAX_RECENT),
    [entries, recentIds],
  );

  useEffect(() => {
    if (!query.trim()) {
      noResultsTracked.current = false;
      return;
    }

    if (results.length > 0) {
      noResultsTracked.current = false;
    } else if (!noResultsTracked.current) {
      noResultsTracked.current = true;
      trackGAEvent(GAMap.header.search.noResults);
    }
  }, [query, results]);

  const rememberEntry = useCallback((entry: SearchEntry) => {
    setRecentIds((previous) => {
      const next = [entry.id, ...previous.filter((id) => id !== entry.id)].slice(0, MAX_RECENT);
      persistStored(RECENT_STORAGE_KEY, next);
      return next;
    });
    setUsageCounts((previous) => {
      const next = { ...previous, [entry.id]: (previous[entry.id] ?? 0) + 1 };
      persistStored(USAGE_STORAGE_KEY, next);
      return next;
    });
  }, []);

  const navigateTo = useCallback(
    (entry: SearchEntry) => {
      setQuery("");
      setOpen(false);
      setDrawerOpen(false);
      rememberEntry(entry);
      trackGAEvent(
        entry.kind === "create" ? GAMap.header.search.create : GAMap.header.search.select,
      );

      if (
        layoutContext.isMobileRequest &&
        entry.mobileModule &&
        entry.href !== "#" &&
        !entry.id.startsWith("sidepanel_settings") &&
        typeof window.replaceModuleWords === "function"
      ) {
        window.replaceModuleWords(entry.mobileModule);
        return;
      }

      if (entry.openInNewTab) {
        window.open(entry.href, "_blank", "noopener,noreferrer");
        return;
      }

      window.location.href = entry.href;
    },
    [layoutContext.isMobileRequest, rememberEntry],
  );

  const focusSearch = useCallback(() => {
    trackGAEvent(GAMap.header.search.open);

    if (compact) {
      setDrawerOpen(true);
      window.setTimeout(() => inputRef.current?.focus(), 0);
      return;
    }

    setOpen(true);
    inputRef.current?.focus();
  }, [compact]);

  useEffect(() => {
    const handleKeyboardShortcut = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        focusSearch();
      }
    };

    window.addEventListener("keydown", handleKeyboardShortcut);
    return () => window.removeEventListener("keydown", handleKeyboardShortcut);
  }, [focusSearch]);

  return {
    query,
    setQuery,
    open,
    setOpen,
    drawerOpen,
    setDrawerOpen,
    inputRef,
    results,
    recentEntries,
    navigateTo,
    focusSearch,
  };
};
